import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Shield, Truck, CreditCard, Minus, Plus, ShoppingCart, Package, CheckCircle } from 'lucide-react';
import { products } from '../data/products';
import { useCart } from '../context/CartContext';
import { ProductCard } from '../components/ProductCard';
export function ProductDetail() {
  const {
    id
  } = useParams();
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'desc' | 'specs' | 'reviews'>('desc');
  const product = products.find(p => p.id === id);
  const relatedProducts = products.filter(p => p.category === product?.category && p.id !== product?.id).slice(0, 4);
  if (!product) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-529">
        <div className="text-center" data-id="element-530">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-531">
            Producto no encontrado
          </h2>
          <Link to="/catalogo" className="text-[#0056b3] hover:underline" data-id="element-532">
            Volver al catálogo
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-[#f8f9fa] min-h-screen" data-id="element-533">
      {/* Breadcrumbs */}
      <div className="bg-white border-b" data-id="element-534">
        <div className="max-w-7xl mx-auto px-4 py-3" data-id="element-535">
          <div className="flex items-center gap-2 text-sm" data-id="element-536">
            <Link to="/" className="text-gray-500 hover:text-[#0056b3]" data-id="element-537">
              Inicio
            </Link>
            <span className="text-gray-300" data-id="element-538">/</span>
            <Link to="/catalogo" className="text-gray-500 hover:text-[#0056b3]" data-id="element-539">
              Productos
            </Link>
            <span className="text-gray-300" data-id="element-540">/</span>
            <span className="text-gray-900 font-medium truncate" data-id="element-541">
              {product.title}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8" data-id="element-542">
        {/* Main Product Section */}
        <div className="bg-white rounded-lg border border-gray-100 p-6 md:p-8 mb-8" data-id="element-543">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-544">
            {/* Image */}
            <div data-id="element-545">
              <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden border border-gray-100 flex items-center justify-center p-8" data-id="element-546">
                <img src={product.imageUrl} alt={product.title} className="max-h-full max-w-full object-contain" data-id="element-547" />
              </div>
            </div>

            {/* Product Info */}
            <div data-id="element-548">
              <span className="text-[#0056b3] font-medium text-sm uppercase tracking-wide" data-id="element-549">
                {product.brand}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 mb-4" data-id="element-550">
                {product.title}
              </h1>

              <div className="flex items-center gap-4 mb-6" data-id="element-551">
                <div className="flex items-center gap-1" data-id="element-552">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} data-id="element-553" />)}
                  <span className="text-sm text-gray-500 ml-2" data-id="element-554">
                    ({product.reviews} reseñas)
                  </span>
                </div>
              </div>

              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-555">
                ${product.price.toFixed(2)}
              </div>

              <div className="flex items-center gap-2 mb-6" data-id="element-556">
                {product.inStock ? <span className="flex items-center gap-1 text-green-600 text-sm" data-id="element-557">
                    <CheckCircle className="w-4 h-4" data-id="element-558" />
                    Disponible en stock
                  </span> : <span className="text-red-500 text-sm" data-id="element-559">Agotado</span>}
              </div>

              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-560">
                {product.description}
              </p>

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6 pb-6 border-b border-gray-100" data-id="element-561">
                <div className="flex items-center border border-gray-200 rounded-md w-fit" data-id="element-562">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-gray-50 text-gray-600" data-id="element-563">
                    <Minus className="w-4 h-4" data-id="element-564" />
                  </button>
                  <span className="px-4 font-medium w-12 text-center" data-id="element-565">
                    {quantity}
                  </span>
                  <button onClick={() => setQuantity(quantity + 1)} className="p-3 hover:bg-gray-50 text-gray-600" data-id="element-566">
                    <Plus className="w-4 h-4" data-id="element-567" />
                  </button>
                </div>
                <button onClick={() => addToCart(product, quantity)} disabled={!product.inStock} className={`flex-1 py-3 px-6 rounded-md font-medium flex items-center justify-center gap-2 transition-colors ${product.inStock ? 'bg-[#0056b3] text-white hover:bg-[#004494]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-568">
                  <ShoppingCart className="w-5 h-5" data-id="element-569" />
                  Agregar al Carrito
                </button>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-570">
                <div className="flex items-center gap-2 text-gray-600" data-id="element-571">
                  <Package className="w-5 h-5 text-[#0056b3]" data-id="element-572" />
                  <span data-id="element-573">Producto Original</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600" data-id="element-574">
                  <Shield className="w-5 h-5 text-[#0056b3]" data-id="element-575" />
                  <span data-id="element-576">Garantía del Fabricante</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600" data-id="element-577">
                  <Truck className="w-5 h-5 text-[#0056b3]" data-id="element-578" />
                  <span data-id="element-579">Envío a todo el país</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600" data-id="element-580">
                  <CreditCard className="w-5 h-5 text-[#0056b3]" data-id="element-581" />
                  <span data-id="element-582">Pago Seguro</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden mb-8" data-id="element-583">
          <div className="flex border-b" data-id="element-584">
            <button onClick={() => setActiveTab('desc')} className={`px-6 py-3 font-medium text-sm transition-colors ${activeTab === 'desc' ? 'border-b-2 border-[#0056b3] text-[#0056b3] bg-blue-50' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-585">
              Descripción
            </button>
            <button onClick={() => setActiveTab('specs')} className={`px-6 py-3 font-medium text-sm transition-colors ${activeTab === 'specs' ? 'border-b-2 border-[#0056b3] text-[#0056b3] bg-blue-50' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-586">
              Especificaciones
            </button>
            <button onClick={() => setActiveTab('reviews')} className={`px-6 py-3 font-medium text-sm transition-colors ${activeTab === 'reviews' ? 'border-b-2 border-[#0056b3] text-[#0056b3] bg-blue-50' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-587">
              Reseñas ({product.reviews})
            </button>
          </div>
          <div className="p-6" data-id="element-588">
            {activeTab === 'desc' && <div className="text-gray-600 leading-relaxed" data-id="element-589">
                <p data-id="element-590">{product.description}</p>
                {product.specifications && product.specifications.length > 0 && <ul className="mt-4 space-y-2" data-id="element-591">
                      {product.specifications.map((spec, i) => <li key={i} className="flex items-center gap-2" data-id="element-592">
                          <CheckCircle className="w-4 h-4 text-green-500" data-id="element-593" />
                          {spec}
                        </li>)}
                    </ul>}
              </div>}
            {activeTab === 'specs' && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl" data-id="element-594">
                {product.material && <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-595">
                    <span className="font-medium text-gray-900" data-id="element-596">Material</span>
                    <span className="text-gray-600" data-id="element-597">{product.material}</span>
                  </div>}
                {product.dimensions && <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-598">
                    <span className="font-medium text-gray-900" data-id="element-599">
                      Dimensiones
                    </span>
                    <span className="text-gray-600" data-id="element-600">{product.dimensions}</span>
                  </div>}
                {product.weight && <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-601">
                    <span className="font-medium text-gray-900" data-id="element-602">Peso</span>
                    <span className="text-gray-600" data-id="element-603">{product.weight}</span>
                  </div>}
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-604">
                  <span className="font-medium text-gray-900" data-id="element-605">SKU</span>
                  <span className="text-gray-600" data-id="element-606">{product.sku}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-607">
                  <span className="font-medium text-gray-900" data-id="element-608">Marca</span>
                  <span className="text-gray-600" data-id="element-609">{product.brand}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-610">
                  <span className="font-medium text-gray-900" data-id="element-611">Categoría</span>
                  <span className="text-gray-600" data-id="element-612">{product.category}</span>
                </div>
              </div>}
            {activeTab === 'reviews' && <div className="text-center py-8 text-gray-500" data-id="element-613">
                <Star className="w-12 h-12 mx-auto mb-4 text-gray-200" data-id="element-614" />
                <p data-id="element-615">Las reseñas estarán disponibles pronto.</p>
              </div>}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && <section data-id="element-616">
            <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-617">
              Productos Relacionados
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-618">
              {relatedProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-619" />)}
            </div>
          </section>}
      </div>
    </div>;
}